import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { forkJoin, of, Subject, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { ApWebcomponentService } from '@ap/webcomponent';

import { ChatNotification } from './conversational.model';

@Component({
    selector: 'mijn-overzicht-template-conversational',
    template: '',
    standalone: false
})
export class ConversationalComponent implements OnInit, OnChanges {
    @Input()
    public changeConversationalStatus$: Subject<string>;

    @Output()
    public chatNotificationSent = new EventEmitter<ChatNotification>();

    private webcomponentTag: HTMLElement;

    private shadowRoot: HTMLElement;

    private conversationalWebcomponentDeployUrl = '/online/webcomponents/mijn-klant-conversational';
    private subscription: Subscription;

    public constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly elementRef: ElementRef,
        private readonly httpClient: HttpClient,
        private readonly apWebcomponentService: ApWebcomponentService
    ) {}

    public ngOnInit(): void {
        this.shadowRoot = this.elementRef.nativeElement;
        this.apWebcomponentService
            .getConfiguration(this.conversationalWebcomponentDeployUrl)
            .pipe(
                switchMap((config) => {
                    const scripts = config.scripts.map((script) =>
                        this.httpClient.get(`${this.conversationalWebcomponentDeployUrl}/${script.name}`, { responseType: 'text' }).pipe(
                            tap((scr) => {
                                const scriptNonce = ((this.document.querySelector('body > script') as HTMLScriptElement) || {}).nonce;
                                // see documentation for script tag https://developer.mozilla.org/en-US/docs/Web/HTML/Element/script
                                const scriptTag: HTMLScriptElement = this.document.createElement('script');
                                scriptTag.innerHTML = scr;
                                scriptTag.setAttribute('nonce', scriptNonce);
                                if (!script.module) {
                                    scriptTag.noModule = true;
                                }
                                this.shadowRoot.appendChild(scriptTag);
                            })
                        )
                    );
                    const style = this.httpClient
                        .get(`${this.conversationalWebcomponentDeployUrl}/${config.style}`, { responseType: 'text' })
                        .pipe(
                            tap((css) => {
                                const styleTag: HTMLStyleElement = this.document.createElement('style');
                                styleTag.textContent = css;
                                this.shadowRoot.appendChild(styleTag);
                            })
                        );

                    return forkJoin({
                        scripts: forkJoin(scripts),
                        style,
                        config: of(config)
                    });
                })
            )
            .subscribe(({ config }) => {
                this.webcomponentTag = this.document.createElement(config.tagName);
                this.webcomponentTag.className = 'ap';

                this.webcomponentTag.addEventListener('chatNotification', (event: CustomEvent) => {
                    this.chatNotificationSent.emit(event.detail as ChatNotification);
                });

                this.shadowRoot.appendChild(this.webcomponentTag);
            });
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (!changes.changeConversationalStatus$) {
            return;
        }

        // no subscription or its closed
        if ((!this.subscription || this.subscription.closed) && changes.changeConversationalStatus$.currentValue) {
            this.registerEventListener();
        } else {
            this.subscription?.unsubscribe();
        }
    }

    private registerEventListener() {
        this.subscription = this.changeConversationalStatus$.subscribe((status) => {
            const conversationalStatusChange = new CustomEvent('conversationalStatusChange', { detail: status });
            this.webcomponentTag.dispatchEvent(conversationalStatusChange);
        });
    }
}
